<template>
  <div
    class="flowchart-container"
    @mousemove="handleMove"
    @mouseup="handleUp"
    @mousedown="handleDown"
  >
    <svg :width="`${height}vw`" :height="`${height}vh`">
    <!-- <svg width="100%" height="100%"> -->
      <flowchart-link
        v-for="(link, index) in lines"
        :key="`link${index}`"
        v-bind.sync="link"
        :style="link.linkStyle"
        @deleteLink="linkDelete(link.id)"
      />
    </svg>
    <flowchart-node
      v-for="node in scene.nodes"
      :key="`node${node.id}`"
      :ref="`node${node.id}`"
      :showLabels="showLabels"
      v-bind.sync="node"
      :options="nodeOptions"
      @linkingStart="linkingStart(node.id)"
      @linkingStop="linkingStop(node.id)"
      @nodeSelected="nodeSelected(node.id, $event)"
      @updateNode="updateNodeLabel(node.id, $event)"
      @dblclick.native="node.edit = true"
      @keyup.enter.native="node.edit = false"
    >
      <slot :node="node"></slot>
    </flowchart-node>
  </div>
</template>

<script>
import FlowchartLink from "./FlowchartLink.vue";
import FlowchartNode from "./FlowchartNode.vue";
import { getMousePosition } from "../../assets/utilty/position";

export default {
  name: "SimpleFlowchart",
  components: {
    FlowchartLink,
    FlowchartNode,
  },
  props: {
    scene: {
      type: Object,
      default() {
        return {
          centerX: 1024,
          scale: 1,
          centerY: 140,
          nodes: [],
          links: [],
        };
      },
    },
    height: {
      type: Number,
      default: 400,
    },
    showLabels: {
      type: Boolean,
    },
  },
  data() {
    return {
      linkStyle: {
        stroke: "rgb(255, 136, 85)",
        strokeWidth: 2.73205,
        fill: "none",
      },
      action: {
        linking: false,
        dragging: false,
        scrolling: false,
        selected: 0,
      },
      mouse: {
        x: 0,
        y: 0,
        lastX: 0,
        lastY: 0,
      },
      draggingLink: null,
      rootDivOffset: {
        top: 0,
        left: 0,
      },
      lines: [],
    };
  },
  computed: {
    nodeOptions() {
      return {
        centerY: this.scene.centerY,
        centerX: this.scene.centerX,
        scale: this.scene.scale,
        offsetTop: this.rootDivOffset.top,
        offsetLeft: this.rootDivOffset.left,
        selected: this.action.selected,
      };
    },
  },
  watch: {
    scene: {
      handler() {
        this.$nextTick(this.setLines);
      },
      deep: true,
      immediate: true,
    },
    draggingLink: {
      handler: "setLines",
      deep: true,
    },
  },
  mounted() {
    this.rootDivOffset.top = this.$el ? this.$el.offsetTop : 0;
    this.rootDivOffset.left = this.$el ? this.$el.offsetLeft : 0;
  },
  methods: {
    setLines() {
      const lines = this.scene.links.map((link) => {
        const fromNode = this.findNodeWithID(link.from);
        const toNode = this.findNodeWithID(link.to);
        let cy, cx, ex, ey;
        [cx, cy] = this.getPortPosition("bottom", fromNode);
        [ex, ey] = this.getPortPosition("top", toNode);
        return {
          start: [cx, cy],
          end: [ex, ey],
          id: link.id,
          linkStyle: {
            stroke: "rgb(255, 136, 85)",
            strokeWidth: 2.73205,
            fill: "none",
          },
        };
      });
      if (this.draggingLink && (this.draggingLink.mx || this.draggingLink.my)) {
        let cy, cx;
        const fromNode = this.findNodeWithID(this.draggingLink.from);
        [cx, cy] = this.getPortPosition("bottom", fromNode);
        // push temp dragging link, mouse cursor postion = link end postion
        lines.push({
          start: [cx, cy],
          end: [this.draggingLink.mx, this.draggingLink.my],
          linkStyle: {
            stroke: "rgb(255, 136, 85)",
            strokeWidth: 2.73205,
            fill: "none",
          },
        });
      }
      this.lines = lines;
    },
    findNodeWithID(id) {
      return this.scene.nodes.find((item) => {
        return id === item.id;
      });
    },
    getPortPosition(type, node) {
      const ref = this.$refs[`node${node.id}`][0];
      const x = this.scene.centerX + node.x;
      const y = this.scene.centerY + node.y;
      if (type === "top") {
        return [x + ref.$el.clientWidth * 0.5, y];
      } else if (type === "bottom") {
        return [x + ref.$el.clientWidth * 0.5, y + ref.$el.clientHeight];
      }
    },
    linkingStart(index) {
      this.action.linking = true;
      this.draggingLink = {
        from: index,
        mx: 0,
        my: 0,
      };
    },
    linkingStop(index) {
      // add new Link
      if (this.draggingLink && this.draggingLink.from !== index) {
        // check link existence
        const existed = this.scene.links.find((link) => {
          return link.from === this.draggingLink.from && link.to === index;
        });
        if (!existed) {
          let maxID = Math.max(
            0,
            ...this.scene.links.map((link) => {
              return link.id;
            })
          );
          const newLink = {
            id: maxID + 1,
            from: this.draggingLink.from,
            to: index,
          };
          this.scene.links.push(newLink);
          this.$emit("linkAdded", newLink);
        }
      }
      this.draggingLink = null;
    },
    linkDelete(id) {
      const deletedLink = this.scene.links.find((item) => {
        return item.id === id;
      });
      if (deletedLink) {
        this.scene.links = this.scene.links.filter((item) => {
          return item.id !== id;
        });
        this.$emit("linkBreak", deletedLink);
      }
    },
    nodeSelected(id, e) {
      this.action.dragging = id;
      this.action.selected = id;
      this.$emit("nodeClick", id);
      this.mouse.lastX =
        e.pageX || e.clientX + document.documentElement.scrollLeft;
      this.mouse.lastY =
        e.pageY || e.clientY + document.documentElement.scrollTop;

      // Highlight all links connected
      var linesConnected = this.scene.links.filter(
        (elem) => elem.from == id || elem.to == id
      );
      for (var line in linesConnected) {
        var item = linesConnected[line];

        let index = this.lines.findIndex((item) => {
          return item.id === linesConnected[line].id;
        });

        this.$set(
          this.lines,
          index,
          Object.assign(this.lines[index], {
            linkStyle: {
              stroke: "rgb(0,223,255)",
              strokeWidth: 2.73205,
              fill: "none",
            },
          })
        );
      }
    },
    nodeDoubleCLicked(id, e) {
      var node = this.scene.nodes.filter((node) => {
        return (node.id = id);
      });
    },
    updateNodeLabel(id, e) {
      let index = this.scene.nodes.findIndex((item) => {
        return item.id === id;
      });
      this.$set(
        this.scene.nodes,
        index,
        Object.assign(this.scene.nodes[index], {
          label: e,
        })
      );
      console.log("edit: " + e + " index: " + id);
    },
    handleMove(e) {
      if (this.action.linking) {
        [this.mouse.x, this.mouse.y] = getMousePosition(this.$el, e);
        [this.draggingLink.mx, this.draggingLink.my] = [
          this.mouse.x,
          this.mouse.y,
        ];
      }
      if (this.action.dragging) {
        this.mouse.x =
          e.pageX || e.clientX + document.documentElement.scrollLeft;
        this.mouse.y =
          e.pageY || e.clientY + document.documentElement.scrollTop;
        let diffX = this.mouse.x - this.mouse.lastX;
        let diffY = this.mouse.y - this.mouse.lastY;

        this.mouse.lastX = this.mouse.x;
        this.mouse.lastY = this.mouse.y;
        this.moveSelectedNode(diffX, diffY);
      }
      if (this.action.scrolling) {
        [this.mouse.x, this.mouse.y] = getMousePosition(this.$el, e);
        let diffX = this.mouse.x - this.mouse.lastX;
        let diffY = this.mouse.y - this.mouse.lastY;

        this.mouse.lastX = this.mouse.x;
        this.mouse.lastY = this.mouse.y;

        this.scene.centerX += diffX;
        this.scene.centerY += diffY;

        // this.hasDragged = true
      }
    },
    handleUp(e) {
      const target = e.target || e.srcElement;
      if (this.$el.contains(target)) {
        if (
          typeof target.className !== "string" ||
          target.className.indexOf("node-input") < 0
        ) {
          this.draggingLink = null;
        }
        if (
          typeof target.className === "string" &&
          target.className.indexOf("node-delete") > -1
        ) {
          this.nodeDelete(this.action.dragging);
        }
      }
      this.action.linking = false;
      this.action.dragging = null;
      this.action.scrolling = false;
    },
    handleDown(e) {
      const target = e.target || e.srcElement;
      if (
        (target === this.$el || target.matches("svg, svg *")) &&
        e.which === 1
      ) {
        this.action.scrolling = true;
        [this.mouse.lastX, this.mouse.lastY] = getMousePosition(this.$el, e);
        this.action.selected = null; // deselectAll
      }
      this.$emit("canvasClick", e);
    },
    moveSelectedNode(dx, dy) {
      let index = this.scene.nodes.findIndex((item) => {
        return item.id === this.action.dragging;
      });
      let left = this.scene.nodes[index].x + dx / this.scene.scale;
      let top = this.scene.nodes[index].y + dy / this.scene.scale;
      this.$set(
        this.scene.nodes,
        index,
        Object.assign(this.scene.nodes[index], {
          x: left,
          y: top,
        })
      );
    },
    nodeDelete(id) {
      this.scene.nodes = this.scene.nodes.filter((node) => {
        return node.id !== id;
      });
      this.scene.links = this.scene.links.filter((link) => {
        return link.from !== id && link.to !== id;
      });
      this.$emit("nodeDelete", id);
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped >
.flowchart-container {
  margin: 0;
  background: #e6e6e6;
  position: relative;
  /* overflow: hidden; */
  overflow-x: scroll;
  overflow-y: scroll;
}
.flowchart-container svg {
  cursor: grab;
}
</style>



// WEBPACK FOOTER //
// src/components/FlowChart/SimpleFlowchart.vue